import React, { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useParams } from 'react-router-dom';
import {
  CsvImportBannerList,
  DashboardListContentPage,
  MainDashboardPage,
  PageTitle,
  Toggle,
  useModalState,
  useToast,
} from 'components';

import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import { NewFeatureBanner } from 'components/NewFeatureBanner';
import { PanelTable } from './PanelTable';
import { PanelSelector } from './components/PanelSelector';
import { DeleteParticipantsModal } from '../Panel/components/DeleteParticipantsModal';
import { InviteParticipantsModal } from '../Panel/components/InviteParticipantsModal';
import { SelectionParticipantFooter } from '../Panel/components/SelectionParticipantFooter';
import {
  useAllParticipantsInPanel,
  useFilterParticipantInPanel,
  useNumberOfPanelParticipants,
} from '@app/api';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

const emptyArray = [] as any[];

export const Panel = () => {
  const classes = useStyles();
  const { id: panelId } = useParams<{ id: string }>();
  const toast = useToast();
  const { t } = useTranslation();

  const { data: participantCount, isLoading: isCountLoading } =
    useNumberOfPanelParticipants(panelId);

  const clientSideComputed = participantCount && participantCount.count < 5000;
  const [clientSideState, setClientSideState] = useState<boolean | undefined>(
    undefined,
  );
  const clientSide =
    clientSideState !== undefined ? clientSideState : clientSideComputed;

  const { isLoading, data } = useAllParticipantsInPanel(
    clientSide ? panelId : undefined,
  );

  const { mutateAsync, isLoading: serverSideIsLoading } =
    useFilterParticipantInPanel(panelId);

  const [selected, setSelected] = React.useState<string[]>([]);
  const [
    toDeleteParticipantsList,
    setToDeleteParticipantsList,
    clearDeleteParticipantList,
  ] = useModalState<string[]>(emptyArray);

  const [
    toInviteParticipantsList,
    setToInviteParticipantsList,
    clearInviteParticipantList,
  ] = useModalState<string[]>(emptyArray);

  const onDelete = useCallback(
    () => setToDeleteParticipantsList(selected),
    [setToDeleteParticipantsList, selected],
  );

  const onInvite = useCallback(() => {
    setToInviteParticipantsList(selected);
  }, [setToInviteParticipantsList, selected]);

  const deselectRows = useCallback(
    () => setSelected(emptyArray),
    [setSelected],
  );

  const serverSiderSource = useCallback(
    async (args: any) => {
      try {
        const { filterValue, limit, skip } = args;
        const res = await mutateAsync({
          filter: filterValue,
          limit,
          skip,
        });
        return {
          data: res.data,
          count: res.total,
        };
      } catch (e) {
        toast.exception(e);
        return {
          data: [],
          count: 0,
        };
      }
    },
    [mutateAsync, toast],
  );

  const dataSource = clientSide ? data : serverSiderSource;

  return (
    <AnalyticsCategoryProvider category={Categories.PANEL}>
      <PageTitle title={t('participants.metaTitle')} />
      <MainDashboardPage>
        <NewFeatureBanner />

        <DashboardListContentPage
          title={t('participants.list.title')}
          banner={<CsvImportBannerList />}
          fetching={isLoading || isCountLoading || serverSideIsLoading}
          onClick={() => {}}
          titleSideElement={<PanelSelector selected={panelId} />}
          actions={[
            <div>
              <Toggle
                key="toggle"
                label="Filter on browser"
                checked={clientSide}
                onToggle={setClientSideState}
              />
            </div>,
          ]}
          emptyListContent={<div />}
          className={classes.content}
        >
          {!!panelId && dataSource && (
            <PanelTable
              panelId={panelId}
              selected={selected}
              onSelectedChange={setSelected}
              dataSource={dataSource}
              key={`${panelId}_${clientSide}`}
            />
          )}

          <SelectionParticipantFooter
            selectedRowIds={selected}
            onDelete={onDelete}
            onInvite={onInvite}
            takeSpace
          />
          {panelId && (
            <DeleteParticipantsModal
              isOpen={!!toDeleteParticipantsList.length}
              onRequestClose={clearDeleteParticipantList}
              toDeleteParticipantsList={toDeleteParticipantsList}
              onDeleteSuccess={deselectRows}
              panelId={panelId}
            />
          )}
          <InviteParticipantsModal
            isOpen={!!toInviteParticipantsList.length}
            onRequestClose={clearInviteParticipantList}
            participants={toInviteParticipantsList}
            onInviteSuccess={deselectRows}
          />
        </DashboardListContentPage>
      </MainDashboardPage>
    </AnalyticsCategoryProvider>
  );
};
